import { InitiateSSOResponse } from '@app/api/initiate-sso/InitiateSSOResponse';
import { DomainCheckResponse } from '@app/api/login/domain-check/DomainCheckResponse';
import axios from 'axios';
import { useState, useCallback, useRef, useEffect } from 'react';

import { useLoginForm } from './LoginFormContext';
import { SSO_CALLBACK_PATH } from './sso-callback/ssoHandoff';

export interface UseSSO {
    isSSOEnabled: boolean;
    isCheckingSSO: boolean;
    isRedirectingToSSO: boolean;
    ssoError: string | null;
    checkSSODomain: (email: string) => Promise<void>;
    initiateSSOLogin: (email: string) => Promise<void>;
    disableSSO: () => void;
}

export function useSSO(initialSsoError?: string): UseSSO {
    const [isSSOEnabled, setIsSSOEnabled] = useState(false);
    const [isCheckingSSO, setIsCheckingSSO] = useState(false);
    const [isRedirectingToSSO, setIsRedirectingToSSO] = useState(false);
    const [ssoError, setSsoError] = useState<string | null>(initialSsoError ?? null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const { externalAuthId } = useLoginForm();

    const checkSSODomain = useCallback(
        async (email: string): Promise<void> => {
            // Don't enable SSO if there was a previous SSO error (prevents infinite loop)
            if (initialSsoError) {
                setIsSSOEnabled(false);
                return;
            }

            if (!email.includes('@')) {
                setIsSSOEnabled(false);
                return;
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            setIsCheckingSSO(true);
            setSsoError(null);

            try {
                // Call server-side API to check SSO eligibility
                // This keeps the SSO domain list server-side and doesn't expose it to clients
                const response = await axios.post<DomainCheckResponse>(
                    '/api/login/domain-check',
                    { email },
                    { signal: abortController.signal },
                );

                if (!abortController.signal.aborted) {
                    setIsSSOEnabled(response.data.isEligible);
                    setIsCheckingSSO(false);
                }
            } catch (error: unknown) {
                if (!axios.isCancel(error) && !abortController.signal.aborted) {
                    setIsSSOEnabled(false);
                    setIsCheckingSSO(false);
                }
            }
        },
        [initialSsoError],
    );

    const initiateSSOLogin = useCallback(
        async (email: string): Promise<void> => {
            setIsRedirectingToSSO(true);
            setSsoError(null);

            try {
                const intendedRedirect = new URLSearchParams(window.location.search).get('redirectAfterLogin');

                // The server hands these to the callback in cookies; see /api/initiate-sso.
                const response = await axios.post<InitiateSSOResponse>('/api/initiate-sso', {
                    email,
                    redirectUrl: `${window.location.origin}${SSO_CALLBACK_PATH}`,
                    externalAuthId,
                    redirectAfterLogin: intendedRedirect ?? undefined,
                });

                if (response.data.authUrl) {
                    window.location.href = response.data.authUrl;
                } else {
                    setSsoError('Failed to get authorization URL. Please try again.');
                    setIsRedirectingToSSO(false);
                }
            } catch (error) {
                if (axios.isAxiosError(error) && error.response?.data && typeof error.response.data === 'object') {
                    const errorData = error.response.data as { error?: string };
                    setSsoError(errorData.error ?? 'Failed to initiate SSO login. Please try again.');
                } else {
                    setSsoError('Failed to initiate SSO login. Please try again.');
                }
                setIsRedirectingToSSO(false);
            }
        },
        [externalAuthId],
    );

    const disableSSO = useCallback((): void => {
        setIsSSOEnabled(false);
        setSsoError(null);
        setIsRedirectingToSSO(false);
    }, []);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return {
        isSSOEnabled,
        isCheckingSSO,
        isRedirectingToSSO,
        ssoError,
        checkSSODomain,
        initiateSSOLogin,
        disableSSO,
    };
}
